import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Laptop, Smartphone } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { changePasswordInput, updateProfileInput, type SessionInfo } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, errorMessage } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { meKey, needsPasswordChange, useSession } from '@/lib/session';
import { PageHeader } from '@/components/shared/page-header';
import { applyServerError, zodForm } from '../catalog/lib';
import { Section } from './section';

const schema = changePasswordInput.extend({ confirm: z.string() }).refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: 'As senhas não conferem' });
type Values = z.input<typeof schema>;

function describeAgent(ua: string | null) {
  if (!ua) return { name: 'Dispositivo desconhecido', mobile: false };
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Navegador';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad|iOS/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'sistema desconhecido';
  return { name: `${browser} em ${os}`, mobile: /Android|iPhone|iPad/.test(ua) };
}

/** Página "Minha conta": dados de acesso, senha e aparelhos conectados. Vale para qualquer papel. */
export function AccountPage() {
  const tz = useTimezone();
  const { me } = useSession();
  return (
    <div className="grid max-w-3xl gap-10">
      <PageHeader title="Minha conta" description="Seus dados de acesso valem para todas as barbearias em que você participa." />
      {needsPasswordChange(me) && <MustChangePasswordBanner />}
      <ProfileForm />
      <Section title="Senha" description="Ao trocar a senha, os outros aparelhos são desconectados.">
        <PasswordForm />
      </Section>
      <SessionsSection tz={tz} />
    </div>
  );
}

/** Faixa exibida enquanto o operador mantém a senha provisória do usuário. */
export function MustChangePasswordBanner() {
  return (
    <div role="alert" className="rounded-md border border-brass/50 bg-brass/10 p-3 text-sm">
      <strong>Defina uma nova senha para continuar.</strong> Sua senha foi definida pela administração da plataforma. Informe-a como senha atual e escolha uma nova na seção Senha, abaixo.
    </div>
  );
}

const profileSchema = updateProfileInput.extend({ currentPassword: z.string().max(128).optional() });
type ProfileValues = z.input<typeof profileSchema>;

export function ProfileForm() {
  const { me, org } = useSession();
  const qc = useQueryClient();
  const form = useForm<ProfileValues>({
    resolver: zodForm<ProfileValues>(profileSchema),
    values: { name: me?.user.name ?? '', email: me?.user.email ?? '', currentPassword: '' },
  });
  const { errors } = form.formState;
  const email = form.watch('email');
  const emailChanged = Boolean(me && email.trim().toLowerCase() !== me.user.email.toLowerCase());
  const save = useMutation({
    mutationFn: (v: ProfileValues) => api.patch<{ emailChanged: boolean }>('/auth/profile', { name: v.name, email: v.email, currentPassword: v.currentPassword || undefined }),
    onSuccess: (r) => {
      toast.success(r.emailChanged ? 'Dados salvos. Os outros aparelhos foram desconectados.' : 'Dados salvos');
      qc.invalidateQueries({ queryKey: meKey });
      qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
    onError: (e) => {
      if (e instanceof Error && 'code' in e && (e as { code: string }).code === 'invalid_password') form.setError('currentPassword', { message: e.message });
      else if (e instanceof Error && 'code' in e && (e as { code: string }).code === 'email_unavailable') form.setError('email', { message: e.message });
      else applyServerError(form, e, {});
    },
  });
  return (
    <Section title="Dados de acesso" description="Nome e e-mail usados para entrar e para receber avisos do sistema.">
      <form
        onSubmit={form.handleSubmit((v) => {
          if (emailChanged && !v.currentPassword) {
            form.setError('currentPassword', { message: 'Informe a senha atual para trocar o e-mail' });
            return;
          }
          save.mutate(v);
        })}
        className="grid max-w-md gap-4"
        noValidate
      >
        <Field id="pf-name" label="Nome" error={errors.name?.message}>
          <Input id="pf-name" autoComplete="name" aria-invalid={!!errors.name} {...form.register('name')} />
        </Field>
        <Field id="pf-email" label="E-mail" error={errors.email?.message}>
          <Input id="pf-email" type="email" autoComplete="email" aria-invalid={!!errors.email} {...form.register('email')} />
        </Field>
        {emailChanged && (
          <Field id="pf-pass" label="Senha atual" error={errors.currentPassword?.message} hint="Necessária para trocar o e-mail. Os outros aparelhos serão desconectados.">
            <Input id="pf-pass" type="password" autoComplete="current-password" aria-invalid={!!errors.currentPassword} {...form.register('currentPassword')} />
          </Field>
        )}
        {org && (
          <p className="text-sm text-muted-foreground">
            Seu papel em {org.organizationName}: <span className="font-medium text-foreground">{org.roleName}</span>. Quem altera papéis e permissões é o proprietário.
          </p>
        )}
        <div>
          <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
            {save.isPending ? 'Salvando…' : 'Salvar dados'}
          </Button>
        </div>
      </form>
    </Section>
  );
}

export function PasswordForm() {
  const qc = useQueryClient();
  const form = useForm<Values>({ resolver: zodForm<Values>(schema), defaultValues: { currentPassword: '', newPassword: '', confirm: '' } });
  const { errors } = form.formState;
  const save = useMutation({
    mutationFn: (v: Values) => api.post('/auth/change-password', { currentPassword: v.currentPassword, newPassword: v.newPassword }),
    onSuccess: () => {
      toast.success('Senha alterada. As outras sessões foram encerradas.');
      form.reset();
      qc.invalidateQueries({ queryKey: meKey });
      qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
    onError: (e) => {
      if (e instanceof Error && 'code' in e && (e as { code: string }).code === 'invalid_password') form.setError('currentPassword', { message: e.message });
      else applyServerError(form, e, { newPassword: 'newPassword' });
    },
  });
  return (
    <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid max-w-md gap-4" noValidate>
      <Field id="pw-current" label="Senha atual" error={errors.currentPassword?.message}>
        <Input id="pw-current" type="password" autoComplete="current-password" aria-invalid={!!errors.currentPassword} {...form.register('currentPassword')} />
      </Field>
      <Field id="pw-new" label="Nova senha" error={errors.newPassword?.message} hint="Mínimo de 8 caracteres, com letras e números.">
        <Input id="pw-new" type="password" autoComplete="new-password" aria-invalid={!!errors.newPassword} {...form.register('newPassword')} />
      </Field>
      <Field id="pw-confirm" label="Repita a nova senha" error={errors.confirm?.message}>
        <Input id="pw-confirm" type="password" autoComplete="new-password" aria-invalid={!!errors.confirm} {...form.register('confirm')} />
      </Field>
      <div>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Salvando…' : 'Alterar senha'}
        </Button>
      </div>
    </form>
  );
}

/** `tz` vem de fora: o operador da plataforma não tem organização para consultar o fuso. */
export function SessionsSection({ tz }: { tz: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['auth', 'sessions'], queryFn: () => api.get<SessionInfo[]>('/auth/sessions') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
  const others = q.data?.filter((s) => !s.current) ?? [];

  const revoke = async (s: SessionInfo) => {
    try {
      await api.del(`/auth/sessions/${s.id}`);
      toast.success('Sessão encerrada');
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <Section
      title="Sessões ativas"
      description="Aparelhos em que sua conta está conectada. Encerre os que você não reconhece."
      actions={
        others.length > 0 && (
          <ConfirmDialog
            trigger={<Button variant="outline">Encerrar todas as outras</Button>}
            title="Encerrar as outras sessões?"
            description="Todos os outros aparelhos serão desconectados. Esta sessão continua ativa."
            confirmLabel="Encerrar sessões"
            onConfirm={async () => {
              try {
                await api.post('/auth/sessions/revoke-others');
                toast.success('Sessões encerradas');
                refresh();
              } catch (e) {
                toast.error(errorMessage(e));
              }
            }}
          />
        )
      }
    >
      {q.isLoading ? (
        <LoadingState />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <ul className="divide-y rounded-lg border bg-card">
          {q.data?.map((s) => {
            const d = describeAgent(s.userAgent);
            const Icon = d.mobile ? Smartphone : Laptop;
            return (
              <li key={s.id} className="flex items-center gap-3 p-4">
                <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {d.name} {s.current && <Badge variant="secondary">Esta sessão</Badge>}
                  </p>
                  <p className="tabular text-xs text-muted-foreground">
                    {s.ip ?? 'IP desconhecido'} · último uso {fmtDateTime(s.lastUsedAt, tz)}
                  </p>
                </div>
                {!s.current && (
                  <ConfirmDialog
                    trigger={<Button variant="outline" size="sm">Encerrar</Button>}
                    title="Encerrar esta sessão?"
                    description={`${d.name} será desconectado.`}
                    confirmLabel="Encerrar sessão"
                    onConfirm={() => revoke(s)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
